/**
 * Storage request throttling specifically for Supabase Storage requests
 * Prevents excessive requests to the same resource
 */

interface StorageThrottleEntry {
  lastRequest: number;
  requestCount: number;
  blockedCount: number;
}

class StorageThrottle {
  private requests: Map<string, StorageThrottleEntry> = new Map();
  private readonly minInterval: number;
  private readonly maxRequestsPerMinute: number;

  constructor(minInterval: number = 10000, maxRequestsPerMinute: number = 3) {
    this.minInterval = minInterval;
    this.maxRequestsPerMinute = maxRequestsPerMinute;
  }

  /**
   * Check if a storage request should be allowed
   * @param url - The full storage URL
   * @returns true if request should be allowed, false if throttled
   */
  shouldAllowRequest(url: string): boolean {
    // Extract filename from URL for throttling key
    const filename = url.substring(url.lastIndexOf('/') + 1);
    const now = Date.now();
    const entry = this.requests.get(filename);

    if (!entry) {
      this.requests.set(filename, {
        lastRequest: now,
        requestCount: 1,
        blockedCount: 0
      });
      console.log(`[StorageThrottle] ✓ Allowing first request: ${filename}`);
      return true;
    }

    const timeSinceLastRequest = now - entry.lastRequest;

    // Check minimum interval
    if (timeSinceLastRequest < this.minInterval) {
      entry.blockedCount++;
      console.warn(`[StorageThrottle] ⛔ Blocked: ${filename} - too soon (${Math.round(timeSinceLastRequest / 1000)}s < ${this.minInterval / 1000}s, blocked ${entry.blockedCount} times)`);
      return false;
    }

    // Reset counter if it's been more than a minute
    const oneMinuteAgo = now - 60000;
    if (entry.lastRequest < oneMinuteAgo) {
      entry.requestCount = 0;
      entry.blockedCount = 0;
    }

    // Check rate limit
    if (entry.requestCount >= this.maxRequestsPerMinute) {
      entry.blockedCount++;
      console.warn(`[StorageThrottle] ⛔ Blocked: ${filename} - rate limit (${entry.requestCount}/${this.maxRequestsPerMinute} per minute, blocked ${entry.blockedCount} times)`);
      return false;
    }

    // Allow the request
    entry.lastRequest = now;
    entry.requestCount++;
    this.requests.set(filename, entry);

    console.log(`[StorageThrottle] ✓ Allowing request: ${filename} (${entry.requestCount}/${this.maxRequestsPerMinute} this minute)`);
    return true;
  }

  /**
   * Mark a resource as accessed without creating a network request
   * Useful for cache hits
   */
  markAccessed(url: string): void {
    const filename = url.substring(url.lastIndexOf('/') + 1);
    const now = Date.now();
    const entry = this.requests.get(filename);

    if (!entry) {
      this.requests.set(filename, {
        lastRequest: now,
        requestCount: 0,
        blockedCount: 0
      });
    } else {
      entry.lastRequest = now;
    }
  }

  reset(url?: string): void {
    if (url) {
      const filename = url.substring(url.lastIndexOf('/') + 1);
      this.requests.delete(filename);
    } else {
      this.requests.clear();
    }
  }

  getStats() {
    const stats: Record<string, StorageThrottleEntry & { timeSince: number }> = {};
    const now = Date.now();

    this.requests.forEach((entry, key) => {
      stats[key] = {
        ...entry,
        timeSince: now - entry.lastRequest
      };
    });

    return stats;
  }

  printStats(): void {
    const stats = this.getStats();
    console.log('[StorageThrottle] === Storage Request Stats ===');
    Object.entries(stats).forEach(([filename, data]) => {
      console.log(`  ${filename}:`);
      console.log(`    - Last request: ${Math.round(data.timeSince / 1000)}s ago`);
      console.log(`    - Requests this minute: ${data.requestCount}`);
      console.log(`    - Total blocked: ${data.blockedCount}`);
    });
  }
}

// Strict throttling: 10 seconds between requests, max 3 per minute
export const storageThrottle = new StorageThrottle(10000, 3);

// Make available in window for debugging
if (typeof window !== 'undefined') {
  (window as any).__storageThrottle = storageThrottle;
  (window as any).__printStorageStats = () => storageThrottle.printStats();
}
